import {
    RestaurantMenuOutlined as RecipesIcon,
    PlayArrowOutlined as VideoContentIcon,
    VideocamOutlined as TextVideoContentIcon,
    NotesOutlined as TextContentIcon,
    InsertDriveFileOutlined as PdfContentIcon,
} from "@mui/icons-material";
import {getRecipes, getRecipeById, saveRecipe, updateRecipe, deleteRecipe} from "../../services/RecipeService";
import {getRecipeCategories, saveRecipeCategory, updateRecipeCategory, deleteRecipeCategory} from "../../services/RecipeCategoryService";
import {getRecipeTags, saveRecipeTag, updateRecipeTag, deleteRecipeTag} from "../../services/RecipeTagService";
import {MAX_DIRECT_VIDEO_UPLOAD_MB} from "../../utils/videoUtils";

export const RECIPE_CONTENT_TYPES = {
    PDF: "PDF",
    VIDEO: "VIDEO",
    TEXT: "TEXT",
    TEXT_VIDEO: "TEXT_VIDEO",
};

const recipesConfig = {
    key: "recipes",
    // Video bytes upload directly to S3 before the recipe metadata is saved.
    videoUploadMaxSizeMb: MAX_DIRECT_VIDEO_UPLOAD_MB,
    mainIcon: RecipesIcon,
    // Opt-in content-type capability (Guides omits this and keeps its original behavior).
    // Order is tuned for the RTL 2x2 card grid: row 1 = וידאו / וידאו + טקסט, row 2 = PDF / טקסט.
    contentTypes: [
        {value: RECIPE_CONTENT_TYPES.VIDEO, label: "וידאו", icon: VideoContentIcon},
        {value: RECIPE_CONTENT_TYPES.TEXT_VIDEO, label: "וידאו + טקסט", icon: TextVideoContentIcon},
        {value: RECIPE_CONTENT_TYPES.PDF, label: "PDF", icon: PdfContentIcon},
        {value: RECIPE_CONTENT_TYPES.TEXT, label: "טקסט", icon: TextContentIcon},
    ],
    defaultContentType: RECIPE_CONTENT_TYPES.TEXT,
    // Recipes don't use the short description field at all (Guides keeps it).
    showDescription: false,
    // Short description is optional for recipes (required by default elsewhere, e.g. Guides).
    descriptionRequired: false,
    // Recipe body uses a rich-text (HTML) editor so coaches can format the content
    // (headings, lists, colors, links). Rendered with the same formatting on mobile.
    richTextContent: true,
    // Opt-in nutrition section (measurement units + custom values). Guides omits this.
    hasNutrition: true,
    // The four core macros captured per measurement unit (grams). Labels are compact
    // because they render as inline inputs inside each unit card.
    // `pointsKey` is the macro's name in nutritionUnits: a company that counts in points types
    // these in points (stored as grams) under `pointsLabel`. Fibre has no points value — no key.
    nutritionMacros: [
        {key: "protein", label: "חלבון (ג')", pointsKey: "protein", pointsLabel: "חלבון (נק׳)"},
        {key: "carbs", label: "פחמימות (ג')", pointsKey: "carbs", pointsLabel: "פחמימות (נק׳)"},
        {key: "fat", label: "שומן (ג')", pointsKey: "fats", pointsLabel: "שומן (נק׳)"},
        {key: "fiber", label: "סיבים (ג')", optional: true},
    ],
    // Recipe categories are managed by name only in admin — hide the icon picker/column.
    showCategoryIcons: false,
    // Dedicated food-themed palette for recipe categories (Ionicons, rendered on web + mobile).
    defaultCategoryIcon: "restaurant-outline",
    iconOptions: [
        {value: "flame-outline", label: "בקר"},
        {value: "restaurant-outline", label: "עוף"},
        {value: "fish-outline", label: "דגים"},
        {value: "cafe-outline", label: "חלבי"},
        {value: "egg-outline", label: "פרווה"},
        {value: "pizza-outline", label: "פחמימות"},
        {value: "ice-cream-outline", label: "קינוחים"},
        {value: "wine-outline", label: "משקאות"},
        {value: "leaf-outline", label: "סלטים"},
        {value: "bonfire-outline", label: "מרקים"},
        {value: "nutrition-outline", label: "צמחוני"},
        {value: "flower-outline", label: "טבעוני"},
        {value: "sunny-outline", label: "ארוחת בוקר"},
        {value: "fast-food-outline", label: "חטיפים"},
        {value: "water-outline", label: "רטבים"},
        {value: "basket-outline", label: "תוספות"},
    ],
    service: {
        getList: getRecipes,
        getById: getRecipeById,
        save: saveRecipe,
        update: updateRecipe,
        remove: deleteRecipe,
    },
    categoryService: {
        getCategories: getRecipeCategories,
        saveCategory: saveRecipeCategory,
        updateCategory: updateRecipeCategory,
        deleteCategory: deleteRecipeCategory,
    },
    tagService: {
        getTags: getRecipeTags,
        saveTag: saveRecipeTag,
        updateTag: updateRecipeTag,
        deleteTag: deleteRecipeTag,
    },
    labels: {
        pageTitle: "מתכונים",
        pageSubtitle: "ניהול מתכונים וקטגוריות",
        newItemButton: "מתכון חדש",
        newCategoryButton: "קטגוריה חדשה",
        tabMain: "מתכונים",
        tabCategories: "קטגוריות",
        tabTags: "תגיות",
        newTagButton: "תגית חדשה",
        tagsCardTitle: "תגיות מתכונים",
        tagsLabel: "תגיות",
        allTags: "כל התגיות",
        tagDeleteConfirm: "מחיקת תגית תסיר אותה מכל המתכונים המשויכים. להמשיך?",
        countSuffix: "מתכונים",
        searchPlaceholder: "חיפוש מתכון...",
        emptyTitle: "אין מתכונים",
        emptyHint: 'לחץ על "מתכון חדש" כדי להוסיף',
        allCategories: "כל הקטגוריות",
        contentTypeLabel: "אופן הצגת המתכון",
        textBodyLabel: "תוכן המתכון",
        textBodyPlaceholder: "כתבו את אופן ההכנה, המרכיבים והוראות...",
        nutritionTitle: "יחידות מידה וקלוריות",
        nutritionSubtitle: "הגדירו יחידת מידה אחת או יותר לפי המתכון (לדוגמה: 100 גרם, יחידה, כף). לכל יחידה ניתן להזין קלוריות וערכים תזונתיים (חלבון, פחמימות, שומן, סיבים).",
        nutritionRequiredError: "יש להגדיר לפחות יחידת מידה אחת, ולמלא בכל יחידה את השדות: שם, כמות, קלוריות, חלבון, פחמימות ושומן (סיבים אופציונלי).",
        unitNamePlaceholder: "שם היחידה (לדוגמה: כף)",
        unitAmountPlaceholder: "כמות",
        unitCaloriesPlaceholder: "קלוריות",
        addUnitButton: "הוספת יחידת מידה",
        // What the nutrition labels say instead when the company counts in points (נקודות).
        nutritionPoints: {
            nutritionTitle: "יחידות מידה ונקודות",
            nutritionSubtitle: "הגדירו יחידת מידה אחת או יותר לפי המתכון (לדוגמה: 100 גרם, יחידה, כף). לכל יחידה ניתן להזין נקודות וערכים תזונתיים (חלבון, פחמימות ושומן בנקודות, סיבים בגרמים).",
            nutritionRequiredError: "יש להגדיר לפחות יחידת מידה אחת, ולמלא בכל יחידה את השדות: שם, כמות, נקודות, חלבון, פחמימות ושומן (סיבים אופציונלי).",
            unitCaloriesPlaceholder: "נקודות",
        },
        categoriesCardTitle: "קטגוריות מתכונים",
        createSuccessMessage: "המתכון נשמר בהצלחה ונוסף למאגר המזון",
        editSuccessMessage: "המתכון עודכן בהצלחה",
        categoryDeleteConfirm: "מחיקת קטגוריה תסיר אותה מכל המתכונים המשויכים. להמשיך?",
        deleteConfirmText: "אתה בטוח רוצה למחוק את המתכון? פעולה זו לא ניתנת לביטול",
        dialog: {
            viewTitle: "צפייה במתכון",
            editTitle: "עריכת מתכון",
            createTitle: "העלאת מתכון חדש",
            saveButton: "שמירת מתכון",
            titleLabel: "שם",
            titlePlaceholder: "הזינו שם למתכון",
            saveError: "שגיאה בשמירת המתכון",
        },
    },
};

export default recipesConfig;
